import { useState, useEffect, useRef } from "react";
import LoadingSpinner from "../../Universal/LoadingSpinner";
import useDark from "./useDark";
import getTheme from "./theme";
import { TILE_CONFIG } from "./constants";
import TileActionWrapper from "./tiles/TileActionWrapper";

const Grid = ({ tileType, currentCase, baseMargin = 0 }) => {
  const dark = useDark();
  const t = getTheme(dark);
  const [tiles, setTiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(null);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerHeight(window.innerHeight - rect.top);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const config = TILE_CONFIG[tileType];
    if (!config) {
      setTiles([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setTiles([]);
    setIsVisible(false);
    config
      .fetcher(currentCase)
      .then((response) => {
        const data = response?.data;
        if (!data || !Array.isArray(data)) {
          setTiles([]);
          return;
        }
        setTiles(data);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setIsVisible(true)),
        );
      })
      .catch(() => setTiles([]))
      .finally(() => setIsLoading(false));
  }, [tileType, currentCase]);

  const pad = Math.max(baseMargin, 24) + 24;

  if (isLoading) {
    return (
      <div
        ref={containerRef}
        className="flex flex-col font-poppins font-normal pt-14"
        style={{ height: containerHeight || "100%" }}
      >
        <div className="flex flex-1 items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (tiles.length === 0) {
    return (
      <div
        ref={containerRef}
        className="flex flex-col font-poppins font-normal pt-14"
        style={{ height: containerHeight || "100%" }}
      >
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <p className="text-sm text-mutedText">
            No {tileType.toLowerCase()} found
          </p>
        </div>
      </div>
    );
  }

  const config = TILE_CONFIG[tileType];
  const Component = config?.Component;

  const handleTileInteract = (documentId, state) => {
    setTiles((prev) =>
      prev.map((tile) =>
        tile.document_id === documentId ? { ...tile, ...state } : tile,
      ),
    );
  };

  return (
    <div
      ref={containerRef}
      className="font-poppins font-normal"
      style={{ height: containerHeight || "100%", overflowY: "auto" }}
    >
      <div className="pb-8 pt-14">
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(480px, 1fr))",
            paddingLeft: pad,
            paddingRight: pad,
          }}
        >
          {tiles
            .filter((item) => config?.canRender?.(item) ?? true)
            .map((item, index) => (
              <div
                key={item.id || item.document_id || index}
                style={{
                  transition: `all 0.4s ease-out ${Math.min(index * 40, 600)}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(14px)",
                }}
              >
                {config?.noActionWrapper ? (
                  <Component data={item} t={t} />
                ) : (
                  <TileActionWrapper
                    article={item}
                    t={t}
                    onInteract={handleTileInteract}
                  >
                    <Component data={item} t={t} />
                  </TileActionWrapper>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Grid;
